
import { useEffect, useCallback, startTransition } from 'react';
import { useTwilioCallState } from './useTwilioCallState';
import { useTwilioEventListeners } from './twilio/useTwilioEventListeners';
import { useTwilioPermissions } from './useTwilioPermissions';
import { useTwilioConfiguration } from './useTwilioConfiguration';
import { useCallLogSession } from './twilio/useCallLogSession';
import { useTwilioDevice } from './twilio/useTwilioDevice';
import { useTwilioCallStatus } from './twilio/useTwilioCallStatus';
import { useTwilioCallActions } from './twilio/useTwilioCallActions';
import { useTwilioCallLogs } from './useTwilioCallLogs';
import { useAuth } from '@/context/AuthContext';

export const useTwilioCall = () => {
  // Authentication check
  const { isAuthenticated } = useAuth();
  
  // Core state management
  const callState = useTwilioCallState();
  const { 
    callInProgress, 
    setCallInProgress, 
    callStatus, 
    setCallStatus,
    callStartTime,
    setCallStartTime,
    callSid,
    setCallSid,
    isMuted,
    setIsMuted,
    formattedDuration,
    activeContactId,
    setActiveContactId,
    activeContactName,
    setActiveContactName,
    resetCallState,
    endCallState,
    updateCallState
  } = callState;

  // Add debugging for state changes - enhanced logging
  useEffect(() => {
    console.log('📊 useTwilioCall: State update:', {
      callInProgress,
      callStatus,
      formattedDuration,
      callSid,
      isMuted,
      timestamp: new Date().toISOString()
    });
    
    // Log topbar state specifically
    if (callInProgress) {
      console.log('🔥 TOPBAR STATE: Call in progress detected for topbar display');
    } else {
      console.log('💤 TOPBAR STATE: No active call for topbar');
    }
  }, [callInProgress, callStatus, formattedDuration, callSid, isMuted]);

  // Device and permissions
  const { micPermission, requestPermissions } = useTwilioPermissions();
  const { isTwilioConfigured } = useTwilioConfiguration();
  const { deviceRef, isInitializingDevices, initializeDevice, handleTokenExpiry } = useTwilioDevice(isTwilioConfigured);
  
  // Call logs - NOW USING REAL LOGGING
  const callLogActions = useTwilioCallLogs();
  const { clearSession } = useCallLogSession();
  
  // Status polling
  const { startStatusPolling, stopStatusPolling } = useTwilioCallStatus(
    callStatus,
    setCallStatus,
    endCallState
  );

  // Call actions with integrated logging and token refresh
  const { makeCall, endCall, toggleMute, sendDTMF } = useTwilioCallActions(
    deviceRef,
    callInProgress,
    setCallInProgress,
    setCallStatus,
    setCallStartTime,
    setCallSid,
    setIsMuted,
    setActiveContactId,
    setActiveContactName,
    resetCallState,
    endCallState,
    startStatusPolling,
    stopStatusPolling,
    initializeDevice,
    callLogActions,
    handleTokenExpiry // Pass the token expiry handler
  );
  
  // Event listeners with real logging
  useTwilioEventListeners({
    localCallSidRef: { current: null },
    callLogIdRef: { current: null },
    callState,
    callStateActions: {
      updateCallState: callState.updateCallState,
      endCallState,
      resetCallState
    },
    callTimers: {
      startDurationTimer: () => {},
      stopPolling: () => {}
    },
    callLogActions // Pass real logging functions
  });

  // Device is initialized lazily on first call (user gesture) to avoid AudioContext warnings

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      console.log('🧹 useTwilioCall unmounting.');
      stopStatusPolling();
      startTransition(() => {
        resetCallState();
      });
    };
  }, [resetCallState, stopStatusPolling]);

  // Dismiss call: fully clears call state + transcript (user clicked close or starting new call)
  const dismissCall = useCallback(() => {
    console.log('🗑️ Dismissing call - full state clear');
    resetCallState();
  }, [resetCallState]);

  return {
    // Call state
    callInProgress,
    callStatus,
    callStartTime,
    callSid,
    isMuted,
    formattedDuration,
    activeContactId,
    activeContactName,
    
    // Device state
    isInitializingDevices,
    micPermission,
    
    // Actions
    makeCall,
    endCall,
    toggleMute,
    sendDTMF,
    requestPermissions,
    initializeDevice,
    dismissCall,
  };
};
